<template>
	<div class="add-elements-container">
		<div class="mini-card mt-10">
			<div class="mini-header"><p>{{ getString('add_elements') }}</p></div>
			<div class="mini-body">
				<AppList :items="addElements" />
			</div>
		</div>
		<div class="summary-container mt-15">
			<div class="summary-title-container">
				<div class="summary-title">{{ getString('elements_summary') }}</div>
				<AppButtonIcon icon-name="icon-trash-white" prevent-change small :disabled="elements.length === 0" @click-icon="elements = []" />
			</div>
			<div class="table-container">
				<div class="table-header">
					<div class="table-header-item large">{{ getString('name') }}</div>
					<div class="table-header-item">{{ getString('from_number') }}</div>
					<div class="table-header-item">{{ getString('to_number') }}</div>
					<div class="table-header-item">{{ getString('number_people') }}</div>
				</div>
				<div class="table-body" v-if="elements.length > 0">
					<div class="table-row" v-for="(element) in elements" :key="element.id">
						<div class="table-header-item large">{{ element.name }}</div>
						<div class="table-header-item editable" @click="editFrom(element)">
							<input v-if="element.editFrom" :ref="'edit-from-'+element.id" type="number" :value="element.from" @focusout="endEditFrom(element)" placeholder="1" />
							<p v-else>{{ element.from }}</p>
						</div>
						<div class="table-header-item editable" @click="editTo(element)">
							<input v-if="element.editTo" :ref="'edit-to-'+element.id" type="number" :value="element.to" @focusout="endEditTo(element)" placeholder="1" />
							<p v-else>{{ element.to }}</p>
						</div>
						<div class="table-header-item editable" @click="editPeople(element)">
							<input v-if="element.editPeople" :ref="'edit-people-'+element.id" type="number" :value="element.people" @focusout="endEditPeople(element)" placeholder="1" />
							<p v-else>{{ element.people }}</p>
						</div>
					</div>
				</div>
				<div class="no-elements" v-else>
					{{ getString('no_elements') }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import AppList from "@/shared/components/AppList.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores";
import {getRandomAlphanumericString} from "@/shared/helpers/strings";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const notificationStore = useNotificationStore();
		const userStore = useUserStore();
		return {getString: languageStore.getString, notificationStore, userStore};
	},
	emits: ["elements-changed"],
	name: "AddElements",
	components: {AppButtonIcon, AppList},
	data() {
		return {
			elements: [],
			currentNewElement: {
				name: '',
				quantity: 1,
				people: 1
			},
		}
	},
	computed: {
		addElements() {
			return [
				{title: this.getString('element_name'), explain: this.getString('element_name_explain'), textInput: true, placeholder: this.getString('element_name'), value: this.currentNewElement.name, callbackText: (v) => this.currentNewElement.name = v},
				{title: this.getString('how_many_elements'), numberInput: true, min: 1, max: 1000, value: this.currentNewElement.quantity, callbackNumber: (v) => this.currentNewElement.quantity = v},
				{title: this.getString('how_many_people_per_element'), numberInput: true, min: 0, max: 100, value: this.currentNewElement.people, callbackNumber: (v) => this.currentNewElement.people = v},
				{title: '', buttonTitle: this.getString('add'), callbackButton: this.addElement},
			];
		}
	},
	methods: {
		addElement() {

			let from = 1;
			this.elements.forEach((element) => {
				if(element.to > from) {
					from = element.to + 1;
				}
			});

			this.elements.push({
				id: getRandomAlphanumericString(10),
				name: this.currentNewElement.name,
				editFrom: false,
				from: from,
				editTo: false,
				to: from + this.currentNewElement.quantity - 1,
				people: this.currentNewElement.people,
				editPeople: false,
			});
			this.$emit('elements-changed', this.elements);
		},
		endEditFrom(element) {
			element.editFrom = false;
			let from = Number(this.$refs['edit-from-'+element.id][0].value);
			if(from < 1) {
				from = 1;
			} else if(from > 999) {
				from = 999;
			}
			element.from = from;
			if(element.to < from) {
				element.to = from;
			}
		},
		endEditTo(element) {
			element.editTo = false;
			let to = Number(this.$refs['edit-to-'+element.id][0].value);
			if(to < 1) {
				to = 1;
			} else if(to > 1000) {
				to = 1000;
			}
			element.to = to;
		},
		endEditPeople(element) {
			element.editPeople = false;
			let people = Number(this.$refs['edit-people-'+element.id][0].value);
			if(people < 0) {
				people = 0;
			} else if(people > 100) {
				people = 100;
			}
			element.people = people;
		},
		editFrom(element) {
			element.editFrom = true;
			this.$nextTick(() => {
				if(this.$refs['edit-from-'+element.id]) {
					this.$refs['edit-from-'+element.id][0].focus();
				}
			});
		},
		editTo(element) {
			element.editTo = true;
			this.$nextTick(() => {
				if(this.$refs['edit-to-'+element.id]) {
					this.$refs['edit-to-'+element.id][0].focus();
				}
			});
		},
		editPeople(element) {
			element.editPeople = true;
			this.$nextTick(() => {
				if(this.$refs['edit-people-'+element.id]) {
					this.$refs['edit-people-'+element.id][0].focus();
				}
			});
		},
	},
}
</script>

<style scoped lang="scss">
.mini-card {
	display: flex;
	flex-direction: column;
	border-radius: 13px;
	background: var(--color-bg-primary-1);
	box-shadow: 0 2px 3px 0 var(--color-alpha-2) inset;

	.mini-header {
		border-radius: 13px 13px 0 0;
		border-bottom: 1px solid var(--color-alpha-4);
		background: var(--color-bg-primary-2);
		padding: 10px;

		p {
			font-weight: bold;
			font-size: 18px;
			text-align: center;
		}
	}

	.mini-body {
		padding: 10px;
	}
}
.summary-container {

	.summary-title-container {
		display: flex;
		justify-content: space-between;
		align-items: center;

		.summary-title {
			font-weight: bold;
			font-size: 16px;
			text-align: left;
		}
	}


	.table-container {
		margin-top: 10px;
		width: 100%;
		overflow: hidden;
		table-layout: fixed;

		.table-header {
			display: flex;
			flex-direction: row;
			border-bottom: 1px solid var(--color-alpha-2);

			.table-header-item {
				background: var(--color-bg);
				color: var(--color-primary);
				font-size: 14px;
				text-align: left;
				flex: 22%;
				padding: 10px;
				position: relative;
				flex-shrink: 0;
				word-wrap: break-word;
				overflow: hidden;
				white-space: nowrap;
				text-overflow: ellipsis;

				&.large {
					flex: 34%;
				}

				&:after {
					content: '';
					display: block;
					width: 1px;
					height: 100%;
					right: 0;
					top: 0;
					background: var(--color-alpha-2);
					position: absolute;
				}

				&:last-child:after {
					display: none;
				}
			}

		}

		.table-body {
			width: 100%;

			.table-row {
				display: flex;
				flex-direction: row;
				border-bottom: 1px solid var(--color-alpha-2);

				.table-header-item {
					flex-grow: 1;
					padding: 10px;
					text-align: left;
					font-size: 14px;
					color: var(--color-primary);
					position: relative;
					flex: 22%;
					flex-shrink: 0;
					word-wrap: break-word;
					overflow: hidden;
					white-space: nowrap;
					text-overflow: ellipsis;
					display: flex;
					align-items: center;

					p {
						font-size: 14px;
						line-height: unset;
						color: var(--color-primary);
					}

					&.editable {
						cursor: pointer;
						background-color: var(--color-bg-primary-2);

						input {
							width: 100%;
							height: 100%;
							border: none;
							color: var(--color-txt);
							font-size: 14px;
							background-color: var(--color-bg-primary-2);

						}
					}

					&.large {
						flex: 34%;
					}

					&:after {
						content: '';
						display: block;
						width: 1px;
						height: 100%;
						right: 0;
						top: 0;
						background: var(--color-alpha-2);
						position: absolute;
					}

					&:last-child:after {
						display: none;
					}
				}
			}

		}

		.no-elements {
			text-align: center;
			margin-top: 10px;
			font-size: 14px;
		}
	}

}
</style>